import React from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import type { OrderVehicleCardProps } from './OrderVehicles.types';
import useWindowSize from '@/hooks/use-window-size';

const ArrowBackground: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 586 165"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    preserveAspectRatio="none"
  >
    <path
      d="M0 12.5107C0 5.88772 5.36566 0.516958 11.9886 0.510686L551.216 4.74695e-06C556.455 -0.0049568 561.091 3.38923 562.669 8.38472L584.858 78.6111C585.601 80.964 585.601 83.4888 584.858 85.8417L562.664 156.085C561.088 161.072 556.464 164.464 551.233 164.469L12.0114 164.98C5.37951 164.986 0 159.612 0 152.98V82.7568V12.5107Z"
      fill="#F6F9FF"
    />
  </svg>
);

const VehicleInfoContent: React.FC<{
  vehicleDate?: string;
  brandName?: string;
  modelName?: string;
  maskedVin?: string;
}> = ({ vehicleDate, brandName, modelName, maskedVin }) => {
  const hasVehicleInfo = !!(brandName || modelName || maskedVin);

  return (
    <>
      {vehicleDate && (
        <span className="mb-4 inline-block rounded-[5px] bg-text-primary px-4 py-2 font-inter text-xs font-semibold text-white">
          {vehicleDate}
        </span>
      )}

      {hasVehicleInfo ? (
        <>
          {brandName && (
            <div className="flex items-center gap-2 py-1">
              <SVGIcon name="ok-blue" size={20} aria-hidden />
              <span className="font-inter text-sm text-text-primary">
                <span className="font-semibold">Marka:</span> {brandName}
              </span>
            </div>
          )}

          {modelName && (
            <div className="flex items-center gap-2 py-1">
              <SVGIcon name="ok-blue" size={20} aria-hidden />
              <span className="font-inter text-sm text-text-primary">
                <span className="font-semibold">Model:</span> {modelName}
              </span>
            </div>
          )}

          {maskedVin && (
            <div className="flex items-center gap-2 py-1">
              <SVGIcon name="ok-blue" size={20} aria-hidden />
              <span className="font-inter text-sm text-text-primary">
                <span className="font-semibold">VIN:</span> {maskedVin}
              </span>
            </div>
          )}
        </>
      ) : vehicleDate ? (
        <div className="flex items-center gap-2 py-1">
          <SVGIcon name="ok-blue" size={20} aria-hidden />
          <span className="font-inter text-sm text-text-primary">
            <span className="font-semibold">Pojazd z dnia:</span> {vehicleDate}
          </span>
        </div>
      ) : null}
    </>
  );
};

const AppraiserReportPendingNotice: React.FC<{
  companyName?: string | null;
}> = ({ companyName }) => (
  <div className="flex flex-row items-start gap-2 lg:max-w-[80%]">
    <SVGIcon
      name="warning-2"
      fill="vehicleTabs-warningText"
      size={28}
      aria-hidden
      className="mt-0.5 hidden flex-shrink-0 lg:block"
    />
    <p className="flex-1 text-center font-inter text-[13px] leading-[18px] text-vehicleTabs-warningText">
      Rzeczoznawca{companyName ? ` ${companyName}` : ''}{' '}
      <span className="font-bold">nie przeprowadził</span> jeszcze{' '}
      <span className="font-bold">oględzin pojazdu</span> i nie przygotował
      raportu końcowego
    </p>
  </div>
);

export const OrderVehicleCard: React.FC<OrderVehicleCardProps> = ({
  vehicle,
  isArchived = false,
  orderAddons = [],
  isReportReady = false,
  companyName,
}) => {
  // Aktywny link dostępowy jest otwieralny przez klienta nawet zanim pełny
  // raport będzie gotowy — wtedy prezentowane są wyłącznie udostępnione dodatki
  // (raporty cząstkowe). [869dj5h6f]
  const isAccessLinkActive = vehicle.reportAccessLinkStatus === 'ACTIVE';
  const hasReportLink =
    !!vehicle.reportAccessLinkId &&
    (isArchived || isReportReady || isAccessLinkActive);
  // Link kieruje na ekran logowania linku dostępowego (email + PIN), a nie
  // bezpośrednio na widok raportu — reportAccessLinkId to id linku, nie raportu.
  const reportUrl = hasReportLink
    ? `/report-access/${vehicle.reportAccessLinkId}`
    : undefined;
  // Gdy pełny raport nie jest jeszcze gotowy, dostępne są tylko raporty
  // cząstkowe z usług dodatkowych.
  const showPartialReportText = isArchived || !isReportReady;
  const vehicleDate = vehicle.createdAt
    ? format(vehicle.createdAt, 'dd.MM.yyyy', { locale: pl })
    : undefined;
  const vehicleAddons = vehicle.addons;
  const addons =
    isArchived || vehicleAddons.length > 0 ? vehicleAddons : orderAddons;
  const hasAddons = addons.length > 0;
  const { isMobile } = useWindowSize();
  const showAppraiserReportPendingNotice = !isArchived && !isReportReady;

  return (
    <div
      className={clsx(
        'flex flex-col gap-5 bg-white',
        isArchived
          ? 'rounded-[16px] p-3'
          : 'rounded-[20px] p-4 lg:p-6',
      )}
    >
      {/* Vehicle info + report action row */}
      <div className={clsx('flex flex-col md:flex-row', isArchived ? 'gap-4' : 'gap-6')}>
        {/* Left side - Vehicle info with arrow background */}
        <div
          className={clsx(
            'relative w-full flex-shrink-0',
            isArchived ? 'md:w-[440px]' : 'md:w-[586px]',
          )}
        >
          {!isMobile && <ArrowBackground className="absolute inset-0 h-full w-full" />}
          <div className={clsx('relative', isArchived ? 'md:p-3' : 'md:p-4')}>
            <VehicleInfoContent
              vehicleDate={vehicleDate}
              brandName={vehicle.brandName}
              modelName={vehicle.modelName}
              maskedVin={vehicle.maskedVin}
            />
          </div>
        </div>

        {/* Right side - Report status + button */}
        {hasReportLink && (
          <div className={clsx(
            'flex flex-1 flex-col items-center justify-center',
            isArchived ? 'gap-3 py-2' : 'gap-5 py-4',
          )}>
            <p className={clsx(
              'text-center font-inter text-text-primary',
              isArchived ? 'text-sm' : 'text-base',
            )}>
              {showPartialReportText ? (
                <>
                  Zapoznaj się z{' '}
                  <span className="font-bold">
                    cząstkowymi
                    <br />
                    raportami
                  </span>{' '}
                  z usług dodatkowych
                </>
              ) : (
                <>
                  Zapoznaj się z{' '}
                  <span className="font-bold">pełnym raportem</span>
                  <br />z oględzin pojazdu
                </>
              )}
            </p>

            <a
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                'inline-flex items-center justify-center rounded-button text-white',
                'font-archivo font-semibold uppercase tracking-wider',
                'bg-primary transition-colors hover:bg-primary-600',
                isArchived ? 'px-6 py-2 text-sm' : 'px-8 py-3 text-base',
              )}
            >
              Zobacz wyniki pojazdu
            </a>

            {showAppraiserReportPendingNotice && (
              <AppraiserReportPendingNotice companyName={companyName} />
            )}
          </div>
        )}

        {/* Right side - Waiting for report (no report link, not archived) */}
        {!hasReportLink && !isArchived && (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 py-4">
            <p className="text-center font-inter text-base leading-[24px] text-text-primary">
              W tym miejscu pojawi się
              <br />
              pełny raport dla tego pojazdu
            </p>

            <div className="group relative">
              <button
                disabled
                className={clsx(
                  'inline-flex items-center justify-center rounded-button px-8 py-3',
                  'font-archivo text-base font-semibold uppercase tracking-wider',
                  'cursor-not-allowed bg-primary-300 text-white',
                )}
              >
                Zobacz wyniki pojazdu
              </button>
              <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-text-primary px-3 py-1.5 font-inter text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                Wyniki pojazdu niedostępne.
                <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-text-primary" />
              </div>
            </div>

            {showAppraiserReportPendingNotice && (
              <AppraiserReportPendingNotice companyName={companyName} />
            )}
          </div>
        )}
      </div>

      {/* Addons section */}
      {hasAddons && (
        <div className="flex flex-col gap-3">
          <h4 className="font-archivo text-base font-medium text-text-primary">
            Usługi dodatkowe:
          </h4>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {addons.map((addon, index) => (
              <div
                key={`${addon.type}-${index}`}
                className="flex items-center gap-2.5 rounded-[5px] bg-greenLight px-3.5 py-3 text-greenDark"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0C3.13409 0 0 3.13409 0 7C0 10.8659 3.13409 14 7 14C10.8659 14 14 10.8659 14 7C14 3.13409 10.8659 0 7 0ZM10.0342 5.81636C10.0901 5.7525 10.1326 5.67811 10.1593 5.59756C10.186 5.51702 10.1963 5.43195 10.1896 5.34735C10.183 5.26276 10.1594 5.18036 10.1205 5.10499C10.0815 5.02962 10.0278 4.96281 9.96262 4.90849C9.89744 4.85417 9.82204 4.81343 9.74088 4.78868C9.65972 4.76392 9.57442 4.75565 9.49002 4.76434C9.40561 4.77304 9.32379 4.79853 9.24938 4.83931C9.17497 4.88009 9.10947 4.93535 9.05673 5.00182L6.32036 8.28482L4.90445 6.86827C4.78443 6.75235 4.62369 6.68821 4.45684 6.68966C4.28998 6.69111 4.13038 6.75804 4.01239 6.87602C3.8944 6.99401 3.82748 7.15362 3.82603 7.32047C3.82458 7.48732 3.88872 7.64807 4.00464 7.76809L5.91373 9.67718C5.97625 9.73967 6.05111 9.78845 6.13354 9.8204C6.21596 9.85236 6.30413 9.86679 6.39244 9.86278C6.48075 9.85877 6.56726 9.8364 6.64644 9.7971C6.72563 9.75781 6.79576 9.70245 6.85236 9.63455L10.0342 5.81636Z" fill="#79A824" />
                </svg>
                <span className="font-inter text-[11px] font-semibold leading-[20px] tracking-[0.5px]">
                  {addon.displayName || addon.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
